import { useApiGet } from '../../../services/tanstackApiService';

// ============================================================================
// API Response Types
// ============================================================================

export interface PharmacyInvoiceInfo {
  invoice_id: number;
  bill_number: string;
  bill_type: string;
  visit_date: string;
  visit_id: number;
  invoice_patient_type: string;
  total_amount: number;
  concession_amount: number;
  concession_reason: string | null;
  discount_amount: number;
  tax_amount: number;
  net_amount: number;
  paid_amount: number;
  balance_amount: number;
  due_date: string | null;
  remark: string | null;
  terms_and_conditions: string | null;
  legal_mentions: string | null;
  comments: string | null;
  status: string;
  patient_id: number;
  patient_name: string;
  patient_mrn: string;
  patient_contact: string;
  patient_age: number;
  patient_gender: string;
  treatment_doctor: string;
  treatment_doctor_id: number;
  created_by: string;
  created_by_id: number;
  created_on: string;
  updated_by: string;
  updated_by_id: number;
  updated_on: string;
  balance_due: boolean;
  is_fully_paid: boolean;
  payment_percentage: number;
}

export interface PatientInfo {
  patient_name: string;
  age: number;
  sex: string;
  mrn: string;
  contact_no: string;
  treating_doctor: string;
  treating_doctor_id: number;
  visit_date: string;
  appointment_id: number;
  patient_id: number;
}

export interface PharmacyItem {
  pharmacy_item_id: number;
  invoice_id: number;
  prescription_id: number;
  item_id: number;
  item_code: string;
  item_name: string;
  manufacturer: string;
  manufacturing_company_id: number | null;
  prescribed_strength: string | null;
  prescribed_uom: string;
  billed_strength: string | null;
  billed_uom: string;
  prescribed_quantity: number;
  billed_quantity: number;
  remaining_quantity: number;
  unit_price: number;
  base_mrp: number;
  total_mrp_amount: number;
  total_charges: number;
  discount_amount: number;
  concession_amount: number;
  concession_percentage: number;
  tax_amount: number;
  net_amount: number;
  sgst_percentage: number;
  sgst_amount: number;
  cgst_percentage: number;
  cgst_amount: number;
  igst_percentage: number;
  igst_amount: number;
  batch_number: string;
  batch_code: string | null;
  expiry_date: string;
  rack: string | null;
  shelf: string | null;
  container: string | null;
  rack_id: number | null;
  shelf_id: number | null;
  container_id: number | null;
  pregnancy_class: string | null;
  pregnancy_class_id: number | null;
  visit_date: string;
  provider: string;
  provider_id: number;
  dispensed_by: string;
  dispensed_by_id: number;
  created_by: string;
  created_by_id: number;
  created_on: string;
  updated_by: string;
  updated_by_id: number;
  updated_on: string;
  is_partial_dispensing: boolean;
  patient: {
    id: number;
    name: string;
  };
  bill_number: string;
  item_strength: string;
  package_id?: number | null;
  package_name?: string | null;
}

export interface PharmacyInvoiceMetadata {
  current_prescription_count: number;
  previous_prescription_count: number;
  generated_on: string;
  invoice_id: number;
  bill_number: string;
  invoice_status: string;
}

export interface PharmacyInvoiceDetailsResponse {
  status: boolean;
  message: string;
  data: {
    invoice_info: PharmacyInvoiceInfo;
    patient_info: PatientInfo;
    current_prescriptions: PharmacyItem[];
    previous_prescriptions: PharmacyItem[];
    metadata: PharmacyInvoiceMetadata;
  };
}

// ============================================================================
// API Hooks
// ============================================================================

/**
 * Hook to fetch pharmacy invoice details
 * @param invoiceId - The pharmacy invoice ID
 * @returns Query result with pharmacy invoice details
 */
export function usePharmacyInvoiceDetails(invoiceId?: string | number) {
  return useApiGet<PharmacyInvoiceDetailsResponse>(
    ['pharmacyInvoice', 'details', invoiceId],
    {
      path: `pharmacy-invoice/${invoiceId}/invoice-details/`,
    },
    {
      enabled: !!invoiceId,
    }
  );
}
